import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useUser } from '../../context/UserContext';
import api from '../../services/api';
import { useFilter } from '../../context/FilterContext';
import { Dropdown } from 'bootstrap';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import { useI18n } from '../../i18n/I18nContext'; // Import the translation hook

function VehiculeList() {
  const [vehicules, setVehicules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [hoveredId, setHoveredId] = useState(null);
  const navigate = useNavigate();
  const { t } = useI18n(); // Get the translation function

  const { selectedTransporteurId } = useFilter();

  useEffect(() => {
    fetchVehicules();
    // Initialize all dropdowns
    const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
    dropdownElementList.forEach(dropdownToggleEl => {
      new Dropdown(dropdownToggleEl);
    });
  }, [selectedTransporteurId]);

  const handleRowClick = (id) => {
    navigate(`/vehicules/edit/${id}`);
  };

  const fetchVehicules = async () => {
    try {
      const response = await api.get('/transporteur/vehicules', {
        params: { transporteurId: selectedTransporteurId }
      });
      setVehicules(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm(t('vehicles.confirmDelete'))) {
      try {
        await api.delete(`/transporteur/vehicules/${id}`);
        fetchVehicules(); // Refresh the list
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleStatusChange = async (e, vehicleId, newStatus) => {
    e.stopPropagation();
    try {
      await api.put(`/transporteur/vehicules/${vehicleId}/status`, null, { params: { status: newStatus } });
      fetchVehicules(); // Refresh the list
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-success';
      case 'UNAVAILABLE':
        return 'bg-danger';
      case 'IN_MAINTENANCE':
        return 'bg-warning';
      case 'IN_USE':
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return t('vehicles.status.available');
      case 'UNAVAILABLE':
        return t('vehicles.status.unavailable');
      case 'IN_MAINTENANCE':
        return t('vehicles.status.inMaintenance');
      case 'IN_USE':
        return t('vehicles.status.inUse');
      default:
        return status;
    }
  };

  const filteredVehicules = vehicules.filter(vehicule => {
    const matchesSearch = 
      vehicule.plateNumber.toLowerCase().includes(search.toLowerCase()) ||
      vehicule.model.toLowerCase().includes(search.toLowerCase()) ||
      vehicule.brand.toLowerCase().includes(search.toLowerCase()) ||
      vehicule.type.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = status === 'all' || vehicule.status === status;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mt-4">
        <PageHeader
          title={t('vehicles.title')}
          subtitle={t('vehicles.subtitle')}
          buttonText={t('vehicles.addNew')}
          buttonTo="/vehicules/create"
        />

        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <FilterCard>
          <div className="row g-3">
            <div className="col-md-8">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('vehicles.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">{t('vehicles.status.all')}</option>
                <option value="AVAILABLE">{t('vehicles.status.available')}</option>
                <option value="UNAVAILABLE">{t('vehicles.status.unavailable')}</option>
                <option value="IN_MAINTENANCE">{t('vehicles.status.inMaintenance')}</option>
                <option value="IN_USE">{t('vehicles.status.inUse')}</option>
              </select>
            </div>
          </div>
        </FilterCard>

        <div className="c-table-card card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="c-table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('vehicles.table.plateNumber')}</th>
                    <th>{t('vehicles.table.brand')}</th>
                    <th>{t('vehicles.table.model')}</th>
                    <th>{t('vehicles.table.type')}</th>
                    <th>{t('vehicles.table.capacity')}</th>
                    <th>{t('vehicles.table.status')}</th>
                    <th className="text-end">{t('vehicles.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicules.map((vehicule) => (
                    <tr 
                      key={vehicule.id}
                      onClick={() => handleRowClick(vehicule.id)}
                      onMouseEnter={() => setHoveredId(vehicule.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td data-label={t('vehicles.table.plateNumber')}>
                        <div className="d-flex align-items-center">
                          <div className="avatar-circle bg-primary text-white me-2">
                            <i className="bi bi-truck"></i>
                          </div>
                          <div>
                            <div className="fw-medium">{vehicule.plateNumber}</div>
                          </div>
                        </div>
                      </td>
                      <td data-label={t('vehicles.table.brand')}>{vehicule.brand}</td>
                      <td data-label={t('vehicles.table.model')}>{vehicule.model}</td>
                      <td data-label={t('vehicles.table.type')}>{vehicule.type}</td>
                      <td data-label={t('vehicles.table.capacity')}>{vehicule.capacity} {vehicule.capacityUnit}</td>
                      <td data-label={t('vehicles.table.status')}>
                        <span className={`badge rounded-pill ${getStatusBadgeClass(vehicule.status)}`}>
                          {getStatusLabel(vehicule.status)}
                        </span>
                      </td>
                      <td data-label={t('vehicles.table.actions')}>
                        <div className="d-flex justify-content-end gap-2">
                          <div className="dropdown">
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="btn btn-sm btn-outline-secondary dropdown-toggle"
                              data-bs-toggle="dropdown"
                              data-bs-auto-close="true"
                              aria-expanded="false"
                              title={t('vehicles.actions.changeStatus')}
                            >
                              <i className="bi bi-gear"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">
                              <li>
                                <button 
                                  className="dropdown-item" 
                                  onClick={(e) => handleStatusChange(e, vehicule.id, 'AVAILABLE')}
                                  disabled={vehicule.status === 'AVAILABLE'}
                                >
                                  <i className="bi bi-check-circle text-success me-2"></i>
                                  {t('vehicles.actions.setAvailable')}
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item" 
                                  onClick={(e) => handleStatusChange(e, vehicule.id, 'UNAVAILABLE')}
                                  disabled={vehicule.status === 'UNAVAILABLE'}
                                >
                                  <i className="bi bi-x-circle text-danger me-2"></i>
                                  {t('vehicles.actions.setUnavailable')}
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item" 
                                  onClick={(e) => handleStatusChange(e, vehicule.id, 'IN_MAINTENANCE')}
                                  disabled={vehicule.status === 'IN_MAINTENANCE'}
                                >
                                  <i className="bi bi-tools text-warning me-2"></i>
                                  {t('vehicles.actions.setInMaintenance')}
                                </button>
                              </li>
                              <li>
                                <button 
                                  className="dropdown-item" 
                                  onClick={(e) => handleStatusChange(e, vehicule.id, 'IN_USE')}
                                  disabled={vehicule.status === 'IN_USE' || !vehicule.affectationMission}
                                >
                                  <i className="bi bi-truck text-info me-2"></i>
                                  {t('vehicles.actions.setInUse')}
                                </button>
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button
                                  className="dropdown-item text-danger"
                                  onClick={(e) => handleDelete(e, vehicule.id)}
                                >
                                  <i className="bi bi-trash me-2"></i>
                                  {t('vehicles.actions.delete')}
                                </button>
                              </li>
                            </ul>
                          </div>
                           <button
                              onClick={(e) => handleDelete(e, vehicule.id)}
                              className="btn btn-sm btn-outline-danger"
                              title={t('vehicles.actions.delete')}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredVehicules.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="text-muted">
                          <i className="bi bi-truck me-2"></i>
                          {t('vehicles.noVehiclesFound')}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default VehiculeList;